import { fromEntries, fx, keys } from '@fxts/core';
import { KeyMapType } from '../../../../../../shared/type/util.type.ts';
import { AllProductTypes } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/types.ts';
import { Paths } from '../../../../../../shared/type/ObjectPaths.ts';
import {
  MAX_PURCHASE_PER_USER__MAX,
  PRODUCT_PRICE__MAX,
  PRODUCT_PRICE__MIN,
  FUNDING_GOAL_AMOUNT__MAX,
} from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/constants.ts';
import { commify } from '@services/MarppleShop/shared/util/commify.ts';

export const keyNameFormPathMap = {
  is_force_out_of_stock: 'is_force_out_of_stock',
  sp_cate_root_code: 'sp_cate_root_code',
  sp_cate_list_code: 'sp_cate_list_code',
  sp_cate_item_code: 'sp_cate_item_code',
  membership_ids: 'membership_ids',
  name: '_.product.name',
  name_en: '_.product.name_en',
  name_jp: '_.product.name_jp',
  is_public: '_.product.is_public',
  profit: '_.product.profit',
  max_purchase_per_user: 'max_purchase_per_user',
  sell_start_at: 'sell_start_at',
  sell_end_at: 'sell_end_at',
  spo_items: '_.product._.spo._.spo_items',
  spo_text_options: '_.product._.spo._.spo_text_options',
  spo_options: '_.product._.spo._.spo_options',
  stock_type: '_.product._.spo.stock_type',
  meta: 'meta',
  limited: 'meta.limited',
  adult: 'meta.adult',
  target_amount: 'meta.funding.target_amount',
  description: 'meta.funding.description',
  low_stock_threshold: 'meta.low_stock_threshold',
  max_days: '_.product._.spo._.spo_producing_period.max_days',
  product_story: '_.product._.product_story',
  product_story_images: '_.product._.product_story._.product_story_images',
  product_story_description: '_.product._.product_story.description',
  product_story_description_en: '_.product._.product_story.description_en',
  product_story_description_jp: '_.product._.product_story.description_jp',
  product_basic_info: 'product_basic_info',
  product_basic_info_en: 'product_basic_info_en',
  product_basic_info_jp: 'product_basic_info_jp',
  product_id: 'product_id',
  stores_delivery_companies_products: '_.product._.stores_delivery_companies_products',
  refund_info: 'refund_info',
  refund_info_en: 'refund_info_en',
  refund_info_jp: 'refund_info_jp',
  is_kc_cert_checked: 'is_kc_cert_checked',
  certification_info: 'certification_info',
  qna_type: 'qna_type',
  qna_link: 'qna_link',
  is_use_creator_en: '_.product._.base_product.is_use_creator_en',
  pre_discount_price: '_.product.pre_discount_product.pre_discount_price',
  hs_code: '_.hs_code',
  material: 'material',
  tpl_fee: '_.tpl_fee.ratio',
  seller_profit_share: 'seller_profit_share',
  shipping_date_hidden: 'shipping_date_hidden',
  width: '_.product._.spo._.spo_items',
  height: '_.product._.spo._.spo_items',
  length: '_.product._.spo._.spo_items',
  weight: '_.product._.spo._.spo_items',
  group_name_en: '_.product._.spo._.spo_items',
} as const satisfies Record<string, Paths<AllProductTypes>>;

export type ValidateField = keyof typeof keyNameFormPathMap;

export const formKeyMap = fromEntries(
  fx(keys(keyNameFormPathMap))
    .map((k) => [k, k] as const)
    .toArray(),
) as KeyMapType<KeyMapType<typeof keyNameFormPathMap>>;

export const errorMessage = {
  get required() {
    return '필수 입력 사항입니다.';
  },
  get min_text() {
    return '3자 이상 입력해 주세요.';
  },
  get required_product_name() {
    return '상품명을 입력해 주세요.';
  },
  get required_hs_code() {
    return 'HS CODE를 입력해 주세요.';
  },
  get required_material() {
    return '의류 소재를 입력해 주세요.';
  },
  get invalid_material() {
    return '영문으로 입력해 주세요.';
  },
  get required_refund_info() {
    return '교환/환불 안내를 입력해 주세요.';
  },
  get text_length_100() {
    return '100자 이하로 입력해 주세요.';
  },
  get restrict_character2() {
    return '특수문자는 * - · ( ) [ ] / & + , ~ . : @ ! 만 입력 가능합니다.';
  },
  get black_list_name() {
    return '사용 불가능한 단어가 포함되어 있습니다.';
  },
  get select_category() {
    return '카테고리를 설정해 주세요.';
  },
  get only_number() {
    return '숫자를 입력해 주세요.';
  },
  get min_price() {
    return `최소 ${commify(PRODUCT_PRICE__MIN)}원 이상 입력해 주세요.`;
  },
  get max_price() {
    return `최대 ${commify(PRODUCT_PRICE__MAX)}원 이하로 입력해 주세요.`;
  },
  get max_purchase_per_user() {
    return `최대 ${commify(MAX_PURCHASE_PER_USER__MAX)}개 이하로 입력해 주세요.`;
  },
  get more_than_sell_end_at() {
    return `판매 종료일시를 판매 시작일시보다 이후로 설정해주세요.`;
  },
  get hs_code_length_10() {
    return '숫자 10자리를 입력해 주세요.';
  },
  get goal_amount() {
    return `최대 ${commify(FUNDING_GOAL_AMOUNT__MAX)}원 이하로 입력해 주세요.`;
  },
  get more_than_goal_amount() {
    return '소비자 가격보다 높은 금액을 입력해 주세요.';
  },
  get not_goal_amount() {
    return '목표 판매금액을 입력해 주세요.';
  },
  get duplicated_sku() {
    return '재고관리 번호는 상품을 식별하는 고유 번호이므로\n중복된 재고관리 번호로 등록할 수 없습니다.';
  },
  get not_kc_certification() {
    return 'KC 인증번호를 입력해 주세요.';
  },
  get invalid_qna_sns_link() {
    return '올바른 SNS 주소를 입력해 주세요.';
  },
  get min_marketing_price() {
    return '마케팅 가격은 상품 가격에서 최소 100원 이상이어야 합니다.';
  },
};
